import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardBucket } from '@/api/client';
import { ChartEmpty } from './ChartCard';

// Categorical palette — reads well on both light and dark surfaces. A bucket's own
// `color` (e.g. stage hues) wins; otherwise we assign by index.
export const CHART_PALETTE = [
  '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const AXIS = '#94a3b8'; // slate-400 — legible in both themes
const GRID = 'rgba(148,163,184,0.18)';
const tickProps = { fontSize: 11, fill: AXIS } as const;

const colorAt = (b: DashboardBucket, i: number) => b.color ?? CHART_PALETTE[i % CHART_PALETTE.length];
const unwrap = (d: any): DashboardBucket => (d?.payload ?? d) as DashboardBucket;

type Fmt = (n: number) => string;

function ThemedTooltip({ active, payload, label, valueFormatter }: any & { valueFormatter?: Fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md">
      {label != null && <div className="mb-1 font-medium text-foreground">{label}</div>}
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color ?? p.fill }} />
          <span>{p.name}:</span>
          <span className="font-medium tabular-nums text-foreground">
            {valueFormatter ? valueFormatter(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Bar chart over buckets. `orientation='bars'` lays them out horizontally (good for long labels). */
export function CategoryBar({
  data,
  height = 240,
  orientation = 'columns',
  valueFormatter,
  name = 'Count',
  onSelect,
  labelWidth = 140,
  labelMaxChars,
}: {
  data: DashboardBucket[];
  height?: number;
  orientation?: 'columns' | 'bars';
  valueFormatter?: Fmt;
  name?: string;
  onSelect?: (b: DashboardBucket) => void;
  /** Category-axis width for `bars` (long labels, e.g. client names). */
  labelWidth?: number;
  /** Shorten category labels past this length with an ellipsis; the tooltip keeps the full label. */
  labelMaxChars?: number;
}) {
  const [chartWidth, setChartWidth] = useState(0);
  if (!data.length) return <ChartEmpty height={height} />;
  const horizontal = orientation === 'bars';
  // Column labels tilt only when they'd collide: the slot each column gets vs its longest label (~6.5 px/char at 11 px).
  const slot = chartWidth > 0 ? (chartWidth - 60) / data.length : Infinity;
  const longest = Math.max(...data.map((b) => String(b.label).length));
  // Tilted, neighbouring labels need ~13 px between their baselines: slot × sin(angle) ≥ 13.
  const tilt = slot < longest * 6.5 + 6
    ? -Math.max(15, Math.ceil((Math.asin(Math.min(1, 13 / slot)) * 180) / Math.PI))
    : 0;
  const tiltHeight = Math.round(Math.sin((-tilt * Math.PI) / 180) * longest * 6.5) + 18;
  // ~6 px per character at 11 px, less the tick line and gap.
  const maxChars = labelMaxChars ?? Math.floor((labelWidth - 14) / 6);
  const cursor = onSelect ? { cursor: 'pointer' } : undefined;

  return (
    <ResponsiveContainer width="100%" height={height} onResize={(w) => setChartWidth(w)}>
      <BarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 4, right: 8, bottom: 4, left: horizontal ? 8 : 0 }}
      >
        <CartesianGrid stroke={GRID} vertical={horizontal} horizontal={!horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" tick={tickProps} stroke={AXIS} allowDecimals={false} tickFormatter={valueFormatter} />
            {/* One line per label: recharts wraps a tick at the axis width (splitting "Monitoring Alert" over two
                lines), so the tick gets a generous width and the label is shortened to fit instead. */}
            <YAxis type="category" dataKey="label" tick={{ ...tickProps, width: 1000 }} stroke={AXIS} width={labelWidth} interval={0}
              tickFormatter={(v: string) => (v.length > maxChars ? `${v.slice(0, maxChars - 1)}…` : v)} />
          </>
        ) : (
          <>
            <XAxis dataKey="label" tick={tickProps} stroke={AXIS} interval={0}
              {...(tilt ? { angle: tilt, textAnchor: 'end', height: tiltHeight } : { height: 28 })} />
            <YAxis tick={tickProps} stroke={AXIS} allowDecimals={false} tickFormatter={valueFormatter} />
          </>
        )}
        <Tooltip cursor={{ fill: GRID }} content={<ThemedTooltip valueFormatter={valueFormatter} />} />
        <Bar
          dataKey="value"
          name={name}
          radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          maxBarSize={48}
          onClick={onSelect ? (d: any) => onSelect(unwrap(d)) : undefined}
        >
          {data.map((b, i) => <Cell key={i} fill={colorAt(b, i)} style={cursor} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Donut with a value legend beside it. Both donut segments and legend rows are clickable. */
export function Donut({
  data,
  height = 240,
  valueFormatter,
  onSelect,
}: {
  data: DashboardBucket[];
  height?: number;
  valueFormatter?: Fmt;
  onSelect?: (b: DashboardBucket) => void;
}) {
  if (!data.length || data.every((d) => d.value === 0)) return <ChartEmpty height={height} />;
  const total = data.reduce((s, d) => s + d.value, 0);
  const cursor = onSelect ? { cursor: 'pointer' } : undefined;

  return (
    <div className="flex items-center gap-4" style={{ minHeight: height }}>
      {/* The legend takes its natural width (up to 62%, then truncates) and the ring fills the rest. */}
      <div className="min-w-[96px] flex-1">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            innerRadius="58%"
            outerRadius="92%"
            paddingAngle={2}
            stroke="none"
            onClick={onSelect ? (d: any) => onSelect(unwrap(d)) : undefined}
          >
            {data.map((b, i) => <Cell key={i} fill={colorAt(b, i)} style={cursor} />)}
          </Pie>
          <Tooltip content={<ThemedTooltip valueFormatter={valueFormatter} />} />
        </PieChart>
      </ResponsiveContainer>
      </div>
      <ul className="max-w-[62%] shrink-0 space-y-1.5 text-xs">
        {data.map((b, i) => (
          <li
            key={i}
            className={onSelect ? 'flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/50' : 'flex items-center gap-2'}
            onClick={onSelect ? () => onSelect(b) : undefined}
          >
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: colorAt(b, i) }} />
            <span className="min-w-0 flex-1 truncate text-muted-foreground" title={b.label}>{b.label}</span>
            <span className="font-medium tabular-nums text-foreground">{valueFormatter ? valueFormatter(b.value) : b.value}</span>
            <span className="w-9 text-right tabular-nums text-muted-foreground">{Math.round((b.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Multi-series line chart over a time series. Clicking a point fires onSelectPoint(seriesKey, datum). */
export function TrendLines({
  data,
  xKey,
  series,
  height = 240,
  valueFormatter,
  xTickFormatter,
  onSelectPoint,
  legend,
}: {
  data: Record<string, any>[];
  xKey: string;
  series: { key: string; label: string; color: string }[];
  height?: number;
  valueFormatter?: Fmt;
  xTickFormatter?: (v: any) => string;
  onSelectPoint?: (seriesKey: string, datum: any) => void;
  /** Draw a series legend above the chart (callers with their own legend, e.g. RMM SeriesLegend, leave it off). */
  legend?: boolean;
}) {
  if (!data.length) return <ChartEmpty height={height} />;

  return (
    <>
    {legend && series.length > 1 && (
      <div className="mb-1 flex flex-wrap justify-end gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-3 rounded-full" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
    )}
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} tick={tickProps} stroke={AXIS} tickFormatter={xTickFormatter} />
        <YAxis tick={tickProps} stroke={AXIS} allowDecimals={false} tickFormatter={valueFormatter} width={48} />
        <Tooltip content={<ThemedTooltip valueFormatter={valueFormatter} />} />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            activeDot={onSelectPoint ? { r: 5, style: { cursor: 'pointer' }, onClick: (_e: any, p: any) => onSelectPoint(s.key, p?.payload ?? p) } : { r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
    </>
  );
}
